import { useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

const langToSegment = (code) => (code || "").split("-")[0].toLowerCase();
const hasLangPrefix = (path) => /^\/[a-z]{2}(?=\/|$)/i.test(path);
const isExternal = (url) => /^https?:\/\//i.test(url || "");

export default function useWithLang() {
  const location = useLocation();
  const language = useSelector((s) => s.account?.language);

  const getUrlLangSegment = () =>
    (location.pathname.split("/")[1] || "").toLowerCase();

  const currentLangSegment =
    getUrlLangSegment() || langToSegment(language?.code) || "mk";

  const withLang = (path) => {
    if (!path || typeof path !== "string") return path;
    if (isExternal(path)) return path;

    const p = path.startsWith("/") ? path : `/${path}`;

    if (hasLangPrefix(p)) return p;

    if (p === "/") return `/${currentLangSegment}`;
    return `/${currentLangSegment}${p}`;
  };

  return { withLang, currentLangSegment };
}

export const normalizeInternalPath = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return "/";

  const url = rawUrl.trim();
  if (isExternal(url)) return url;

  const p = url.startsWith("/") ? url : `/${url}`;
  const segments = p.split("/").filter(Boolean);

  const singleSegmentRoutes = new Set([
    "about",
    "confidentiality",
    "documents",
    "document",
    "search",
    "old-documents",
  ]);

  if (
    segments.length === 1 &&
    singleSegmentRoutes.has(segments[0].toLowerCase())
  ) {
    return `/${segments[0]}`;
  }

  if (segments.length >= 2) return p;

  return `/documents/${segments[0]}`;
};

export function useNavigateUrl() {
  const history = useHistory();
  const { withLang } = useWithLang();

  return (rawUrl, opts = { externalTarget: "_self" }) => {
    const normalized = normalizeInternalPath(rawUrl);
    if (!normalized || typeof normalized !== "string") return;

    if (isExternal(normalized)) {
      if (opts.externalTarget === "_blank") window.open(normalized, "_blank");
      else window.location.href = normalized;
      return;
    }

    history.push(withLang(normalized));
  };
}

export function resolveYear(content) {
  if (!content) return content;
  const year = new Date().getFullYear();
  return content.replace(/©\s*\d{4}/g, `© ${year}`);
}
